// Storage backing the persisted user state (redux-persist + userStorageService).
//
// Cookies cannot hold this blob: the holdings list of a real account serializes well past the
// 4 KB per-cookie limit and the browser discards the whole write without raising anything, so
// the selected holding was lost on every reload (CORP-8298). Angular keeps the same blob in
// localStorage (~300 KB on the same account).

const memoryStore = new Map<string, string>();
let available: boolean | null = null;

// Safari private mode and enterprise storage policies make localStorage throw on write. The
// in-memory fallback keeps the app usable for the current tab instead of crashing on boot.
function canUseLocalStorage(): boolean {
  if (available !== null) return available;
  try {
    const probe = '__corp_storage_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    available = true;
  } catch {
    available = false;
  }
  return available;
}

export function getStorageItem(key: string): string | null {
  if (!canUseLocalStorage()) return memoryStore.get(key) ?? null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setStorageItem(key: string, value: string): void {
  if (!canUseLocalStorage()) {
    memoryStore.set(key, value);
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Quota exceeded — keep the value for this tab so the session still behaves.
    memoryStore.set(key, value);
  }
}

export function removeStorageItem(key: string): void {
  memoryStore.delete(key);
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to clean up */
  }
}

export const reduxLocalStorage = {
  getItem(key: string): Promise<string | null> {
    return Promise.resolve(getStorageItem(key));
  },
  setItem(key: string, value: string): Promise<string> {
    setStorageItem(key, value);
    return Promise.resolve(value);
  },
  removeItem(key: string): Promise<void> {
    removeStorageItem(key);
    return Promise.resolve();
  },
};

// Exposed for tests only — the probe result is cached for the lifetime of the module.
export function _resetStorageAvailability(): void {
  available = null;
  memoryStore.clear();
}
